<palantir-dialog [title]="'TAB_WARRANTIES_EQUIPMENT.DIALOG.TITLE' | translate" [formGroup]="form">
  @if (step() === 1) {
    <palantir-dialog-content class="p-0">
      <palantir-input class="mt-6 mx-6" [control]="searchControl" prefix="search-outline">
        <input
          type="text"
          [placeholder]="'TAB_WARRANTIES_EQUIPMENT.DIALOG.SEARCH_BY_NAME' | translate"
          [formControl]="searchControl"
        />
      </palantir-input>

      <palantir-text type="subtitle" size="base" class="ml-6">
        {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.TITLE' | translate }}
      </palantir-text>
      @if (!warrantiesTemplates().length) {
        <palantir-text type="description" size="xs" class="ml-6">
          {{ 'TAB_WARRANTIES_EQUIPMENT.NO_RESULTS.TITLE' | translate }}
        </palantir-text>
      }

      <ng-scrollbar class="w-full flex flex-col max-h-[300px]" [visibility]="'hover'" [autoHeightDisabled]="false">
        <div class="flex flex-col gap-2 p-6 pt-1">
          @if (warrantiesService.isLoading()) {
            <palantir-skeleton class="w-full h-52 rounded-lg"></palantir-skeleton>
            <palantir-skeleton class="w-full h-52 rounded-lg"></palantir-skeleton>
            <palantir-skeleton class="w-full h-52 rounded-lg"></palantir-skeleton>
            <palantir-skeleton class="w-full h-52 rounded-lg"></palantir-skeleton>
            <palantir-skeleton class="w-full h-52 rounded-lg"></palantir-skeleton>
          } @else {
            @for (warrantyTemplate of warrantiesTemplates(); track warrantyTemplate) {
              <palantir-list-item [attr.aria-selected]="isSelected(warrantyTemplate.id)()">
                <palantir-list-item-prefix>
                  <palantir-icon name="warranty-outline"></palantir-icon>
                </palantir-list-item-prefix>
                <palantir-list-item-content>
                  <palantir-text type="title" [truncate]="true">
                    {{ warrantyTemplate.name }} - {{ warrantyTemplate.coverageDurationDays }}
                    {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.DAYS' | translate }}
                  </palantir-text>
                  <palantir-text type="description" [truncate]="true">
                    {{ warrantyTemplate.description }}
                  </palantir-text>
                </palantir-list-item-content>
                <palantir-radio
                  palantir-list-item-suffix
                  (click)="toggleWarrantyTemplate(warrantyTemplate)"
                  [attr.aria-selected]="isSelected(warrantyTemplate.id)()"
                ></palantir-radio>
              </palantir-list-item>
            }
          }
        </div>
      </ng-scrollbar>
    </palantir-dialog-content>
  }
  @if (step() === 2) {
    <palantir-dialog-content class="p-0">
      <palantir-input class="mt-6 mx-6" [control]="searchCompaniesControl" prefix="search-outline">
        <input
          type="text"
          [placeholder]="'TAB_WARRANTIES_EQUIPMENT.DIALOG_COMPANIES.SEARCH' | translate"
          [formControl]="searchCompaniesControl"
        />
      </palantir-input>

      <palantir-text type="subtitle" size="base" class="ml-6">
        {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG_COMPANIES.RESULT_TITLE' | translate }}
      </palantir-text>
      @if (!companies().length) {
        <palantir-text type="description" size="xs" class="ml-6">
          {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG_COMPANIES.NO_RESULTS' | translate }}
        </palantir-text>
      }

      <ng-scrollbar class="w-full flex flex-col max-h-[300px]" [visibility]="'hover'" [autoHeightDisabled]="false">
        <div class="flex flex-col gap-2 p-6 pt-1">
          @for (company of companies(); track $index) {
            <palantir-list-item [attr.aria-selected]="isCompanySelected(company.id)()">
              <palantir-list-item-prefix>
                @if (company.avatarUrl) {
                  <palantir-avatar size="3xs">
                    <img [src]="company.avatarUrl" [alt]="company.name" />
                  </palantir-avatar>
                }
                @if (!company.avatarUrl) {
                  <palantir-avatar size="3xs">
                    {{ company.name }}
                  </palantir-avatar>
                }
              </palantir-list-item-prefix>
              <palantir-list-item-content>
                <palantir-text type="title" [truncate]="true">
                  {{ company.name }}
                </palantir-text>
              </palantir-list-item-content>
              <palantir-radio
                palantir-list-item-suffix
                (click)="toggleCompany(company)"
                [attr.aria-selected]="isCompanySelected(company.id)()"
              ></palantir-radio>
            </palantir-list-item>
          }
        </div>
      </ng-scrollbar>
    </palantir-dialog-content>
  }
  @if (step() === 3) {
    <palantir-dialog-content class="p-0">
      <div class="flex flex-col gap-2 p-6 pt-1">
        <palantir-banner>
          <palantir-icon name="info-outline"></palantir-icon>
          <palantir-text type="description">
            {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.INFO' | translate }}
          </palantir-text>
        </palantir-banner>
        <palantir-content>
          <div class="flex justify-between">
            <palantir-input
              class="w-full"
              [control]="form.get('coverageStartedAt')"
              [label]="'TAB_WARRANTIES_EQUIPMENT.DIALOG.INPUTS.START_WARRANTY' | translate"
              suffix="calendar-outline"
            >
              <input type="date" formControlName="coverageStartedAt" />
              @if (form.get('coverageStartedAt')?.touched) {
                <palantir-input-error>
                  @if (form.get('coverageStartedAt')?.hasError('invalidDate')) {
                    <palantir-text type="negative" size="xs">
                      {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.INPUTS_ERRORS.INVALID_DATE' | translate }}
                    </palantir-text>
                  }
                </palantir-input-error>
              }
            </palantir-input>
            <palantir-input
              class="w-full"
              [attr.disabled]="true"
              [control]="form.get('coverageCompletedAt')"
              [label]="'TAB_WARRANTIES_EQUIPMENT.DIALOG.INPUTS.END_WARRANTY' | translate"
              suffix="calendar-outline"
            >
              <input type="date" formControlName="coverageCompletedAt" />
            </palantir-input>
          </div>
        </palantir-content>
      </div>
    </palantir-dialog-content>
  }

  <palantir-dialog-actions>
    <div class="flex justify-start items-center w-full gap-2">
      <palantir-text type="title"> {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.STEPS' | translate: { step: step() } }} </palantir-text>
      <palantir-progress-bar class="w-1/2 h-2" [progress]="progress()" [mode]="'determinate'"></palantir-progress-bar>
    </div>
    <palantir-button theme="outline" (click)="close()">
      {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.BUTTONS.CANCEL' | translate }}
    </palantir-button>
    @if (step() === 1 || step() === 2) {
      <palantir-button (click)="next()" [attr.disabled]="!selectedWarrantyTemplate()">
        {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.BUTTONS.CONTINUE' | translate }}
      </palantir-button>
    }
    @if (step() === 3) {
      @if (warrantiesService.isLoading()) {
        <palantir-button [attr.disabled]="warrantiesService.isLoading()" icon="loading-outline" layout="prefix">
          {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.BUTTONS.SAVING' | translate }}
        </palantir-button>
      } @else {
        <palantir-button (click)="save()" [attr.disabled]="!form.valid || warrantiesService.isLoading()">
          {{ 'TAB_WARRANTIES_EQUIPMENT.DIALOG.BUTTONS.SAVE' | translate }}
        </palantir-button>
      }
    }
  </palantir-dialog-actions>
</palantir-dialog>
